import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  api,
  formatPKR,
  CATEGORIES,
  BEDDING_TYPES,
  BEDSHEET_SIZES,
  CURTAIN_STANDARD_SIZE,
} from "@/lib/api";
import { BEDDING_TYPE_CONFIG, PILLOW_OPTIONS_BY_SIZE, SET_PC_OPTIONS_BY_SIZE, setSizesFor, pcOptionsFor, effectivePrice, priceWithSale, fromPrice, fromBasePrice, hasAnySale, hasVariantPricing } from "@/lib/beddingConfig";
import { parseDescription } from "@/lib/descriptionFormatter";
import DOMPurify from "dompurify";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Truck, ShieldCheck, Leaf, ArrowLeft } from "lucide-react";

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  // Bedding selectors
  const [type, setType] = useState(null);
  const [size, setSize] = useState(null);
  const [pillows, setPillows] = useState(null);
  const [pcCount, setPcCount] = useState(null);
  const [fabric, setFabric] = useState(null); // curtains
  const [qty, setQty] = useState(1);
  // Sticky CTA bar appears once the main price block scrolls out of view
  const priceRef = useRef(null);
  const galleryTouchX = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      // Auto-select fabric from sub_collection for curtains
      if (r.data?.category === "curtains") {
        if (r.data.sub_collection === "velvet-curtains") setFabric("velvet");
        else if (r.data.sub_collection === "sheer-curtains") setFabric("sheer");
      }
    }).catch(() => setProduct(null)).finally(() => setLoading(false));
    setActiveImg(0);
    setType(null);
    setSize(null);
    setPillows(null);
    setPcCount(null);
    setFabric(null);
    setQty(1);
  }, [id]);

  // Observe the main price block — sticky bar appears once it leaves the viewport
  useEffect(() => {
    if (!product || !priceRef.current) return;
    const el = priceRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [product]);

  const isBedding = product?.category === "bedding";
  const isCurtainGroup = product?.category === "curtains";
  const isCurtain = isCurtainGroup; // all products in curtains category get standard size

  const typeCfg = type ? BEDDING_TYPE_CONFIG[type] : null;

  // Compute the price triple { base, sale, final, onSale, discountPct } for current selection
  const priceInfo = useMemo(() => {
    if (!product) return { base: 0, sale: 0, final: 0, onSale: false, discountPct: 0 };
    if (isBedding) {
      if (!type) {
        const final = hasVariantPricing(product) ? fromPrice(product) : (product.sale_price > 0 && product.sale_price < product.price ? product.sale_price : product.price);
        const base = hasVariantPricing(product) ? fromBasePrice(product) : product.price;
        const onSale = hasAnySale(product);
        const discountPct = onSale && base > 0 ? Math.round(((base - final) / base) * 100) : 0;
        return { base, sale: onSale ? final : 0, final, onSale, discountPct };
      }
      return priceWithSale(product, {
        bedding_type: type,
        pillow_count: typeCfg?.requires_pillow ? pillows : null,
        pc_count: typeCfg?.requires_pc_count ? pcCount : null,
        size: typeCfg?.requires_size ? size : null,
      });
    }
    if (isCurtainGroup) {
      if (!fabric) {
        const final = hasVariantPricing(product) ? fromPrice(product) : (product.sale_price > 0 && product.sale_price < product.price ? product.sale_price : product.price);
        const base = hasVariantPricing(product) ? fromBasePrice(product) : product.price;
        const onSale = hasAnySale(product);
        const discountPct = onSale && base > 0 ? Math.round(((base - final) / base) * 100) : 0;
        return { base, sale: onSale ? final : 0, final, onSale, discountPct };
      }
      return priceWithSale(product, { fabric });
    }
    // Simple categories (blankets, cushion-covers): product-level sale_price
    const base = Number(product.price || 0);
    const saleRaw = Number(product.sale_price || 0);
    const sale = saleRaw > 0 && saleRaw < base ? saleRaw : 0;
    return {
      base,
      sale,
      final: sale > 0 ? sale : base,
      onSale: sale > 0,
      discountPct: sale > 0 ? Math.round(((base - sale) / base) * 100) : 0,
    };
  }, [product, isBedding, isCurtainGroup, type, size, pillows, pcCount, fabric, typeCfg]);

  const livePrice = priceInfo.final;

  const showFromLabel = (isBedding && !type && hasVariantPricing(product)) ||
                        (isCurtainGroup && !fabric && hasVariantPricing(product));

  const canAdd = useMemo(() => {
    if (!product) return false;
    if (isBedding) {
      if (!type) return false;
      if (typeCfg?.requires_pc_count && !pcCount) return false;
      if (typeCfg?.requires_size && !size) return false;
      if (typeCfg?.requires_pillow && !pillows) return false;
      return true;
    }
    if (isCurtainGroup) {
      // If product has variant prices, require fabric selection
      if (hasVariantPricing(product) && !fabric) return false;
      return true;
    }
    return true;
  }, [product, isBedding, isCurtainGroup, type, size, pillows, pcCount, fabric, typeCfg]);

  const handleAdd = () => {
    if (!canAdd || outOfStock) return;
    let config = null;
    if (isBedding) {
      config = {
        bedding_type: type,
        pc_count: typeCfg.requires_pc_count ? pcCount : null,
        size: typeCfg.requires_size ? size : null,
        pillow_count: typeCfg.requires_pillow ? pillows : null,
      };
    } else if (isCurtainGroup) {
      config = { curtain_size: CURTAIN_STANDARD_SIZE, fabric: fabric || null };
    }
    addItem({
      product_id: product.id,
      name: product.name,
      price: livePrice,
      image: product.images?.[0] || "",
      category: product.category,
      config,
      quantity: qty,
    });
    toast.success(`${product.name} added to bag`);
  };

  const outOfStock = Number(product?.stock || 0) <= 0;

  if (loading) return <div className="text-center py-24 text-ink-soft">Loading…</div>;
  if (!product)
    return (
      <div className="text-center py-24" data-testid="product-not-found">
        <h2 className="font-display text-3xl text-ink">Product not found</h2>
        <Link to="/" className="inline-block mt-6 text-sage uppercase tracking-widest text-xs">Back to Home</Link>
      </div>
    );

  const cat = CATEGORIES.find((c) => c.slug === product.category);
  const images = product.images?.length ? product.images : [];

  const onGalleryTouchStart = (e) => { galleryTouchX.current = e.changedTouches[0].clientX; };
  const onGalleryTouchEnd = (e) => {
    if (galleryTouchX.current === null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - galleryTouchX.current;
    galleryTouchX.current = null;
    if (dx <= -40) setActiveImg((i) => (i + 1) % images.length);
    else if (dx >= 40) setActiveImg((i) => (i - 1 + images.length) % images.length);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-6 lg:py-12 pb-28 lg:pb-12" data-testid="product-detail-page">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs text-ink-soft uppercase tracking-widest mb-4 sm:mb-6 min-w-0">
        <Link to="/" className="hover:text-sage shrink-0">Home</Link>
        <ChevronRight size={12} className="shrink-0" />
        <Link to={`/category/${product.category}`} className="hover:text-sage shrink-0" data-testid="breadcrumb-category">
          {cat?.label}
        </Link>
        <ChevronRight size={12} className="shrink-0" />
        <span className="text-ink truncate">{product.name}</span>
      </div>

      <button onClick={() => navigate(-1)} className="lg:hidden mb-3 inline-flex items-center gap-2 min-h-[44px] text-sm text-ink-soft" data-testid="pdp-back">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Gallery */}
        <div data-testid="pdp-gallery" onTouchStart={onGalleryTouchStart} onTouchEnd={onGalleryTouchEnd}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="relative aspect-[4/5] bg-cream-warm overflow-hidden"
          >
            {images.length > 0 ? (
              <img
                src={resolveImage(images[activeImg])}
                alt={product.name}
                className={`w-full h-full object-cover ${outOfStock ? "opacity-60" : ""}`}
                key={activeImg}
              />
            ) : (
              <div className="h-full flex items-center justify-center font-display text-3xl text-sage">
                Bedding Lounge
              </div>
            )}
            {outOfStock && (
              <div
                className="absolute top-4 left-4 bg-red-600 text-cream text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5"
                data-testid="pdp-oos-badge"
              >
                Out of Stock
              </div>
            )}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)}
                  className="hidden sm:block absolute left-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-cream p-2 transition-colors"
                  data-testid="gallery-prev"
                  aria-label="Previous image"
                ><ChevronLeft size={16} /></button>
                <button
                  onClick={() => setActiveImg((i) => (i + 1) % images.length)}
                  className="hidden sm:block absolute right-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-cream p-2 transition-colors"
                  data-testid="gallery-next"
                  aria-label="Next image"
                ><ChevronRight size={16} /></button>
                <div
                  className="sm:hidden absolute bottom-3 right-3 bg-ink/60 text-cream text-[11px] tracking-widest px-2 py-1 rounded-full"
                  data-testid="gallery-counter"
                >
                  {activeImg + 1} / {images.length} · swipe
                </div>
              </>
            )}
          </motion.div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${i === activeImg ? "border-sage" : "border-transparent hover:border-border-subtle"}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={resolveImage(src)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details + Config */}
        <div>
          <div className="label-eyebrow mb-2">{cat?.label}</div>
          <h1 className="font-display text-[1.85rem] leading-[1.12] sm:text-5xl text-ink sm:leading-[1.05] tracking-tight" data-testid="pdp-name">
            {product.name}
          </h1>
          {product.sub_type && (
            <div className="text-sm text-ink-soft mt-2">{product.sub_type}</div>
          )}
          <div ref={priceRef} className="mt-4 flex flex-wrap items-baseline gap-3" data-testid="pdp-price">
            {showFromLabel && <span className="label-eyebrow !text-[10px] !text-ink-soft">From</span>}
            <span className="font-display text-3xl text-ink">{formatPKR(livePrice)}</span>
            {priceInfo.onSale && priceInfo.base > priceInfo.final && (
              <>
                <span className="font-sans text-lg text-ink-soft line-through" data-testid="pdp-base-price">
                  {formatPKR(priceInfo.base)}
                </span>
                <span className="bg-rose-dark text-cream px-2 py-0.5 text-[11px] uppercase tracking-[0.2em] font-bold" data-testid="pdp-discount-badge">
                  −{priceInfo.discountPct}% Off
                </span>
              </>
            )}
          </div>

          {product.description && (
            <FormattedDescription text={product.description} />
          )}

          {outOfStock && (
            <div
              className="mt-6 border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm"
              data-testid="pdp-oos-banner"
            >
              <div className="font-semibold uppercase tracking-widest text-[11px] text-red-700 mb-1">Currently out of stock</div>
              This product is temporarily unavailable. All sizes and variants are sold out. Check back soon — or browse similar items below.
            </div>
          )}

          <div className={outOfStock ? "pointer-events-none opacity-50 select-none" : ""} aria-hidden={outOfStock} data-testid="pdp-config-wrapper">
          {/* Curtain auto-size note */}
          {isCurtain && (
            <div className="mt-6 bg-cream-warm border border-border-subtle px-4 py-3 text-sm text-ink-soft" data-testid="curtain-size-note">
              <span className="label-eyebrow !text-[10px] mr-2">Standard size</span>
              Crafted to <span className="font-semibold text-ink">{CURTAIN_STANDARD_SIZE}</span> — no size selection needed.
            </div>
          )}

          {/* Bedding configuration */}
          {isBedding && (
            <div className="mt-8 space-y-6" data-testid="bedding-config-section">
              <div>
                <div className="label-eyebrow !text-[10px] mb-3">Choose Type</div>
                <div className="grid grid-cols-2 gap-2">
                  {BEDDING_TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => { setType(t); setSize(null); setPillows(null); setPcCount(null); }}
                      className={`relative px-4 py-3 text-sm border transition-all text-left ${
                        type === t ? "border-sage bg-sage text-cream" : "border-border-subtle text-ink hover:border-sage hover:text-sage"
                      }`}
                      data-testid={`pdp-bedding-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {type === t && <Check size={12} className="absolute top-2 right-2" />}
                      <div className="font-semibold">{BEDDING_TYPE_CONFIG[t].label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Type description */}
              {typeCfg && !typeCfg.requires_pc_count && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-cream-warm border-l-2 border-sage pl-4 py-3 pr-4"
                  data-testid="pdp-type-description"
                >
                  <div className="label-eyebrow !text-[10px] mb-1">{typeCfg.label} · Includes</div>
                  <p className="text-ink text-sm leading-relaxed">{typeCfg.description}</p>
                </motion.div>
              )}

              {/* Size selector — Bedsheet, Fitted Sheet, Comforter & Duvet */}
              {typeCfg?.requires_size && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="pdp-size-section">
                  <div className="label-eyebrow !text-[10px] mb-3">Size</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(typeCfg.requires_pc_count ? setSizesFor(product, type) : BEDSHEET_SIZES).map((s) => (
                      <button
                        key={s}
                        onClick={() => { setSize(s); setPillows(null); setPcCount(null); }}
                        className={`px-4 py-3 text-sm border transition-all ${
                          size === s ? "border-rose bg-rose text-cream" : "border-border-subtle text-ink hover:border-rose hover:text-rose"
                        }`}
                        data-testid={`pdp-size-${s.toLowerCase()}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Comforter / Duvet set size — after the bed size is chosen */}
              {typeCfg?.requires_pc_count && size && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="pdp-pc-section">
                  <div className="label-eyebrow !text-[10px] mb-3">Set Size</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(SET_PC_OPTIONS_BY_SIZE[size] || []).map((n) => {
                      const opt = pcOptionsFor(type)[n];
                      return (
                        <button
                          key={n}
                          onClick={() => setPcCount(n)}
                          className={`px-4 py-3 text-sm border transition-all text-left ${
                            pcCount === n ? "border-sage bg-sage text-cream" : "border-border-subtle text-ink hover:border-sage hover:text-sage"
                          }`}
                          data-testid={`pdp-pc-${n}`}
                        >
                          <div className="font-semibold">{opt.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  {pcCount && pcOptionsFor(type)[pcCount] && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 bg-cream-warm border-l-2 border-sage pl-4 py-3 pr-4"
                      data-testid="pdp-pc-description"
                    >
                      <div className="label-eyebrow !text-[10px] mb-1">
                        {size} {pcOptionsFor(type)[pcCount].label} · Includes
                      </div>
                      <p className="text-ink text-sm leading-relaxed">
                        {pcOptionsFor(type)[pcCount].description}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Pillow count — only after size is chosen; options vary by size */}
              {typeCfg?.requires_pillow && size && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="pdp-pillow-section">
                  <div className="label-eyebrow !text-[10px] mb-3">Pillow Count</div>
                  <div className="grid grid-cols-2 gap-2">
                    {(PILLOW_OPTIONS_BY_SIZE[size] || []).map((n) => (
                      <button
                        key={n}
                        onClick={() => setPillows(n)}
                        className={`px-4 py-3 text-sm border transition-all ${
                          pillows === n ? "border-rose bg-rose text-cream" : "border-border-subtle text-ink hover:border-rose hover:text-rose"
                        }`}
                        data-testid={`pdp-pillow-${n}`}
                      >
                        {n} {n === 1 ? "Pillow" : "Pillows"}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Curtain fabric selector */}
          {isCurtainGroup && (
            <div className="mt-8" data-testid="curtain-fabric-section">
              <div className="label-eyebrow !text-[10px] mb-3">Fabric</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "sheer", label: "Sheer", desc: "Light · Airy" },
                  { key: "velvet", label: "Velvet", desc: "Plush · Dramatic" },
                ].map((f) => {
                  const vp = product.variant_prices || {};
                  const priceKey = f.key === "velvet" ? "curtain_velvet" : "curtain_sheer";
                  const hasPrice = Number(vp[priceKey] || 0) > 0;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFabric(f.key)}
                      disabled={!hasPrice && Object.keys(vp).length > 0}
                      className={`relative px-4 py-3 text-sm border transition-all text-left ${
                        fabric === f.key
                          ? "border-rose bg-rose text-cream"
                          : "border-border-subtle text-ink hover:border-rose hover:text-rose disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border-subtle disabled:hover:text-ink"
                      }`}
                      data-testid={`pdp-fabric-${f.key}`}
                    >
                      <div className="font-semibold">{f.label}</div>
                      <div className={`text-xs mt-0.5 ${fabric === f.key ? "text-cream/80" : "text-ink-soft"}`}>{f.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-8 flex items-center gap-4">
            <div className="label-eyebrow !text-[10px]">Qty</div>
            <div className={`flex items-center border border-border-subtle ${outOfStock ? "opacity-50 pointer-events-none" : ""}`}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="min-w-[44px] min-h-[44px] text-lg text-ink-soft hover:text-ink" data-testid="pdp-qty-dec" aria-label="Decrease quantity">−</button>
              <span className="px-4 text-sm" data-testid="pdp-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="min-w-[44px] min-h-[44px] text-lg text-ink-soft hover:text-ink" data-testid="pdp-qty-inc" aria-label="Increase quantity">+</button>
            </div>
          </div>
          </div>{/* end pdp-config-wrapper */}

          {outOfStock ? (
            <Button
              disabled
              className="mt-6 w-full bg-red-600 text-cream py-6 text-xs uppercase tracking-[0.22em] font-semibold cursor-not-allowed opacity-100 hover:bg-red-600"
              data-testid="pdp-out-of-stock-btn"
            >
              Out of Stock
            </Button>
          ) : (
            <Button
              onClick={handleAdd}
              disabled={!canAdd}
              className="mt-6 w-full bg-sage hover:bg-sage-dark text-cream py-6 text-xs uppercase tracking-[0.22em] font-semibold disabled:opacity-50 transition-colors"
              data-testid="pdp-add-to-bag"
            >
              {!canAdd && isBedding ? (
                !type ? "Choose a Type"
                  : typeCfg?.requires_size && !size ? "Choose Size"
                  : typeCfg?.requires_pc_count && !pcCount ? "Choose Set Size"
                  : "Choose Pillow Count"
              ) : !canAdd && isCurtainGroup ? "Choose Fabric"
                : "Add to Bag"}
            </Button>
          )}

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-border-subtle">
            <Trust icon={Truck} text="Cash on Delivery" />
            <Trust icon={Leaf} text="Natural Fibers" />
            <Trust icon={ShieldCheck} text="7-day Returns" />
          </div>
        </div>
      </div>

      {/* Sticky CTA bar — appears when the main price scrolls out of view */}
      <AnimatePresence>
        {showStickyBar && product && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="fixed bottom-0 inset-x-0 z-40 bg-cream/95 backdrop-blur-lg backdrop-saturate-150 border-t border-border-subtle shadow-[0_-4px_24px_-12px_rgba(43,45,42,0.18)]"
            data-testid="pdp-sticky-cta"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-between gap-3 sm:gap-6">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.16em] text-ink-soft font-semibold truncate hidden sm:block">{product.name}</div>
                <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
                  <span className="font-display text-xl sm:text-2xl text-ink leading-none" data-testid="pdp-sticky-price">{formatPKR(livePrice)}</span>
                  {priceInfo.onSale && priceInfo.base > priceInfo.final && (
                    <>
                      <span className="font-sans text-sm text-ink-soft line-through" data-testid="pdp-sticky-base">{formatPKR(priceInfo.base)}</span>
                      <span className="bg-rose-dark text-cream px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold" data-testid="pdp-sticky-discount">
                        −{priceInfo.discountPct}%
                      </span>
                    </>
                  )}
                </div>
              </div>
              {outOfStock ? (
                <Button
                  disabled
                  className="bg-red-600 text-cream px-5 sm:px-8 py-5 sm:py-6 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold cursor-not-allowed opacity-100 hover:bg-red-600 whitespace-nowrap"
                  data-testid="pdp-sticky-oos"
                >
                  Out of Stock
                </Button>
              ) : (
                <Button
                  onClick={handleAdd}
                  disabled={!canAdd}
                  className="bg-sage hover:bg-sage-dark text-cream px-5 sm:px-8 py-5 sm:py-6 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
                  data-testid="pdp-sticky-add-to-bag"
                >
                  {canAdd ? "Add to Bag" : "Choose Options"}
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Trust({ icon: Icon, text }) {
  return (
    <div className="text-center">
      <Icon size={18} className="text-sage mx-auto mb-2" />
      <div className="text-xs text-ink-soft tracking-wide">{text}</div>
    </div>
  );
}

function FormattedDescription({ text }) {
  // 1) If the description was authored in the rich-text editor, it's HTML.
  //    Render it (sanitized) inside a typography wrapper.
  const isHtml = useMemo(() => /<\/?(p|h[1-6]|ul|ol|li|strong|em|u|a|blockquote|hr|br)(\s|>)/i.test(text || ""), [text]);
  const safeHtml = useMemo(
    () =>
      isHtml
        ? DOMPurify.sanitize(text || "", {
            ALLOWED_TAGS: [
              "p", "br", "strong", "b", "em", "i", "u", "s", "strike",
              "h1", "h2", "h3", "h4", "h5", "h6",
              "ul", "ol", "li",
              "blockquote", "hr",
              "a", "code",
            ],
            ALLOWED_ATTR: ["href", "target", "rel", "class"],
          })
        : "",
    [text, isHtml]
  );

  // 2) Legacy plain-text descriptions → smart parser → structured bullets
  const sections = useMemo(() => (isHtml ? [] : parseDescription(text)), [text, isHtml]);

  if (isHtml) {
    return (
      <div
        className="pdp-description mt-6"
        data-testid="pdp-description"
        // safeHtml is pre-sanitized by DOMPurify above with a strict tag/attr allowlist;
        // <script>, <iframe>, on* handlers and javascript: URIs are stripped (XSS-safe).
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  if (!sections.length) return null;
  return (
    <div className="mt-6 space-y-5" data-testid="pdp-description">
      {sections.map((sec, i) => (
        <div key={i} data-testid={`pdp-desc-section-${i}`}>
          {sec.title && (
            <h3 className="font-display text-lg text-ink mb-2 tracking-tight">
              {sec.title}
            </h3>
          )}
          <ul className="space-y-1.5 text-ink-soft text-[15px] leading-relaxed">
            {sec.items.map((item, j) => (
              <li
                key={j}
                className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[0.6em] before:w-1.5 before:h-1.5 before:bg-sage before:rounded-full"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
